import React, { useCallback, useRef, useState } from 'react';

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { RatePlan } from '~/hooks/apis/reservation/usePropertyRatePlans';

import currencyFormatter from '~/utils/currencyFormatter';

import CalendarRateView from './CalendarRateView';
import DateRangeSlider from './DateRangeSlider';
import PrintableChip from './PrintableChip';
import RatePlanInfo from './RatePlanInfo';
import {
  BEST_COMPETITOR_CHART_COLOR,
  CARD_SX,
  CHART_LINE_COLORS,
  LE_CHART_COLOR,
  RATING_BADGE_STYLES,
  SEVERITY_COLORS,
  SUPPLIER_CHIP_COLORS,
} from './constants';
import { ParityAnalysis, RevenueHooks, SeverityLevel, TRACKED_OTAS } from './types';
import { fmtShortDate } from './utils';

function makePriceFormatters(currency: string) {
  return {
    fmtPrice: (n: number) => currencyFormatter(currency, n, 2),
    fmtPriceRounded: (v: number) => currencyFormatter(currency, v, 0),
  };
}

const SECTION_SX = {
  borderRadius: '16px',
  p: 4,
  mb: 3,
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
} as const;

const METRIC_CARD_SX = CARD_SX;

interface Props {
  analysis: ParityAnalysis;
  revenueHooks?: RevenueHooks | null;
  ratePlans?: Array<RatePlan>;
  ratePlansLoading?: boolean;
  ratePlansError?: unknown;
  onGenerateEmail?: () => void;
  onExportPdf?: () => void;
}

function scoreColor(score: number): string {
  if (score >= 80) {
    return SEVERITY_COLORS.in_parity;
  }
  if (score >= 60) {
    return SEVERITY_COLORS.slight;
  }
  if (score >= 40) {
    return SEVERITY_COLORS.moderate;
  }
  return SEVERITY_COLORS.severe;
}

export function computeImpressionParityScore(hooks: RevenueHooks): number | null {
  const total = Number(hooks.totalImpressions?.replace(/,/g, ''));
  const outOfParity = Number(hooks.impressionsOutOfParity?.replace(/,/g, ''));
  if (!total || isNaN(total) || total === 0) {
    return null;
  }
  if (isNaN(outOfParity)) {
    return null;
  }
  return Math.round(((total - outOfParity) / total) * 100);
}

function scoreToRating(score: number): 'GOOD' | 'FAIR' | 'POOR' | 'CRITICAL' {
  if (score >= 80) {
    return 'GOOD';
  }
  if (score >= 60) {
    return 'FAIR';
  }
  if (score >= 40) {
    return 'POOR';
  }
  return 'CRITICAL';
}

function computeInParityImpressions(hooks: RevenueHooks): string {
  const total = Number(hooks.totalImpressions?.replace(/,/g, ''));
  const outOfParity = Number(hooks.impressionsOutOfParity?.replace(/,/g, ''));
  if (isNaN(total) || isNaN(outOfParity)) {
    return '—';
  }
  return Math.round(total - outOfParity).toLocaleString();
}

function renderScoreDonut(score: number, label: string) {
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;
  const color = scoreColor(score);

  return (
    <Box sx={{ flexShrink: 0, textAlign: 'center', position: 'relative', width: 140, height: 140 }}>
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={radius} fill="none" stroke="#e0e0e0" strokeWidth="12" />
        <circle
          cx="70"
          cy="70"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeDasharray={`${filled} ${circumference - filled}`}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round"
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 140,
          height: 140,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="textSecondary">
          {label}
        </Typography>
        <Typography sx={{ fontSize: 36, fontWeight: 700, color }}>{score}%</Typography>
      </Box>
    </Box>
  );
}

const SCORE_CARD_SX = {
  ...METRIC_CARD_SX,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
} as const;

function renderScorePill(ring: React.ReactNode, rating: string, alertText: string) {
  const style = RATING_BADGE_STYLES[rating] ?? RATING_BADGE_STYLES.POOR;
  return (
    <Box sx={SCORE_CARD_SX}>
      <Box sx={{ mb: 1.5 }}>{ring}</Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: style.bgcolor,
          color: style.color,
          borderRadius: '12px',
          fontSize: 13,
          fontWeight: 600,
          width: '100%',
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 20,
            height: 20,
            bgcolor: style.iconBg,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 12,
          }}
        >
          {style.icon}
        </Box>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {alertText}
        </Box>
      </Box>
    </Box>
  );
}

function renderStatCard(label: string, value: string, subtitle: string, valueColor?: string, fontSize = 32) {
  return (
    <Box sx={METRIC_CARD_SX}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ fontSize, fontWeight: 700, mb: 0.5, color: valueColor }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{subtitle}</Typography>
    </Box>
  );
}

export default function ParityReport({
  analysis,
  revenueHooks = null,
  ratePlans = [],
  ratePlansLoading = false,
  ratePlansError,
  onGenerateEmail,
  onExportPdf,
}: Props) {
  const a = analysis;
  const { fmtPrice, fmtPriceRounded } = makePriceFormatters(a.currency);
  const impressionScore = revenueHooks ? computeImpressionParityScore(revenueHooks) : null;
  const chartSectionRef = useRef<HTMLDivElement>(null);
  const [showLegend, setShowLegend] = useState(false);

  const handleDownloadChart = useCallback(async () => {
    if (!chartSectionRef.current) {
      return;
    }
    setShowLegend(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(chartSectionRef.current, { scale: 2, useCORS: true });
    setShowLegend(false);
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `price_comparison_${a.propertyName.replace(/[^a-zA-Z0-9]/g, '_')}_${a.analysisDate}.png`;
    link.click();
  }, [a.propertyName, a.analysisDate]);

  const [channelVisibility, setChannelVisibility] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const ota of TRACKED_OTAS) {
      init[ota] = true;
    }
    return init;
  });

  const totalDays = a.dailyDetail.length;
  const [dateRange, setDateRange] = useState<Array<number>>([0, Math.max(totalDays - 1, 0)]);

  const toggleChannel = (channel: string) => {
    setChannelVisibility((prev) => ({ ...prev, [channel]: !prev[channel] }));
  };

  const visibleDailyDetail = a.dailyDetail.slice(dateRange[0], dateRange[1] + 1);

  const unifiedChartData = visibleDailyDetail.map((d) => {
    const point: Record<string, string | number | null> = {
      date: fmtShortDate(d.date),
      [a.leColumn]: d.lePrice,
      'Best Competitor': d.bestCompetitorPrice,
    };
    for (const ota of TRACKED_OTAS) {
      point[ota] = d.allPrices[ota] ?? null;
    }
    return point;
  });

  const dateRangeCols: Array<GridColDef> = [
    {
      field: 'dateRange',
      headerName: 'Date Range',
      flex: 2,
      valueGetter: (_, row) => `${row.startDate} — ${row.endDate}`,
    },
    { field: 'numDays', headerName: 'Days', width: 70 },
    { field: 'avgLePrice', headerName: 'Avg LE Price', width: 120, valueFormatter: (v: number) => fmtPrice(v) },
    {
      field: 'avgCompetitorPrice',
      headerName: 'Avg Competitor',
      width: 130,
      valueFormatter: (v: number) => fmtPrice(v),
    },
    { field: 'avgDiffPct', headerName: 'Gap', width: 80, valueFormatter: (v: number) => `${v}%` },
    { field: 'competitor', headerName: 'Competitor', flex: 1 },
    {
      field: 'severity',
      headerName: 'Severity',
      width: 110,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <PrintableChip label={params.value} bg={SEVERITY_COLORS[params.value as SeverityLevel]} />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* PDF block 1: start -> before calendar (no page break) */}
      <Box data-pdf-section>
        {/* Section 1: Parity Score Overview */}
        <Paper elevation={0} sx={SECTION_SX}>
          {/* Score Rings Row – each ring + alert in a card; 3 equal columns use full width, alert text wraps */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 3,
              mb: 2,
            }}
          >
            {renderScorePill(
              renderScoreDonut(a.parityScore, 'Date-based'),
              a.parityRating,
              `${a.parityRating}: ${a.inParityCount} of ${a.totalDates} comparable dates at parity`,
            )}
            {impressionScore !== null &&
              revenueHooks &&
              renderScorePill(
                renderScoreDonut(impressionScore, 'Impressions'),
                scoreToRating(impressionScore),
                `${scoreToRating(impressionScore)}: ${computeInParityImpressions(revenueHooks)} of ${
                  revenueHooks.totalImpressions
                } impressions underpriced or in parity`,
              )}
            {(() => {
              const daysWithoutRates = a.severityCounts.no_comparison ?? 0;
              const ringColor = daysWithoutRates === 0 ? SEVERITY_COLORS.in_parity : SEVERITY_COLORS.severe;
              const noRatesRating = daysWithoutRates === 0 ? 'GOOD' : 'CRITICAL';
              const noRatesAlertText =
                daysWithoutRates === 0
                  ? 'GOOD: all dates have rates'
                  : `CRITICAL: ${daysWithoutRates} dates where LE has no rates but competitors do`;
              const noRatesRing = (
                <Box sx={{ flexShrink: 0, textAlign: 'center', position: 'relative', width: 140, height: 140 }}>
                  <svg width="140" height="140" viewBox="0 0 140 140">
                    <circle cx="70" cy="70" r={58} fill="none" stroke="#e0e0e0" strokeWidth="12" />
                    <circle cx="70" cy="70" r={58} fill="none" stroke={ringColor} strokeWidth="12" />
                  </svg>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: 140,
                      height: 140,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      No Rates
                    </Typography>
                    <Typography sx={{ fontSize: 36, fontWeight: 700, color: ringColor }}>{daysWithoutRates}</Typography>
                  </Box>
                </Box>
              );
              return renderScorePill(noRatesRing, noRatesRating, noRatesAlertText);
            })()}
          </Box>

          {/* Date Range Summary */}
          <Typography sx={{ color: 'text.secondary', fontSize: 14, textAlign: 'center', mb: 3 }}>
            {a.dateRangeStart} to {a.dateRangeEnd} &bull; {a.totalCalendarDays} calendar days
          </Typography>

          {/* Metric Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
            {renderStatCard(
              'Avg Price Gap',
              fmtPrice(a.avgPriceDiff),
              `${a.avgPriceDiffPct}% above competitors`,
              undefined,
              24,
            )}
            {renderStatCard(
              'Worst Competitor',
              a.worstCompetitor,
              `Cheapest on ${a.competitorCheapestCounts[a.worstCompetitor] ?? 0} dates`,
              undefined,
              18,
            )}
            {renderStatCard(
              'Severe Issues',
              String(a.severityCounts.severe ?? 0),
              'Dates with >10% gap',
              SEVERITY_COLORS.severe,
              24,
            )}
            {renderStatCard('Out of Parity', String(a.outOfParityCount), `of ${a.totalDates} dates`, undefined, 24)}
          </Box>

          {/* Revenue Alert Banner */}
          {revenueHooks && (
            <Box
              sx={{
                bgcolor: 'error.main',
                color: 'white',
                p: 3,
                borderRadius: '12px',
                mb: 3,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'white' }}>
                ⚠ REVENUE AT RISK:{' '}
                {fmtPriceRounded(Number(String(revenueHooks.estimatedExtraRevenueMonthly).replace(/,/g, '')))} per month
              </Typography>
              <Typography sx={{ fontSize: 14, opacity: 0.95, color: 'white' }}>
                {a.outOfParityCount} days out of parity detected - {revenueHooks.outOfParityPct}% of dates need
                attention
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          {(onGenerateEmail || onExportPdf) && (
            <Box data-html2canvas-ignore sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 4 }}>
              {onGenerateEmail && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ py: 2, borderRadius: '12px', fontSize: 15, fontWeight: 600 }}
                  onClick={onGenerateEmail}
                >
                  Generate Partner Email
                </Button>
              )}
              {onExportPdf && (
                <Button
                  variant="contained"
                  sx={{ py: 2, borderRadius: '12px', fontSize: 15, fontWeight: 600, bgcolor: 'grey.600' }}
                  onClick={onExportPdf}
                >
                  Export Report to PDF
                </Button>
              )}
            </Box>
          )}

          {/* Conversion Impact */}
          {revenueHooks && (
            <>
              <Typography sx={{ fontSize: 24, fontWeight: 600, mb: 3 }}>Your Conversion Impact</Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  gap: 4,
                  alignItems: 'center',
                  p: 4,
                  bgcolor: 'grey.50',
                  borderRadius: '12px',
                  mb: 3,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                    when overpriced
                  </Typography>
                  <Typography sx={{ fontSize: 36, fontWeight: 700, color: 'error.main' }}>
                    {revenueHooks.cvrOutOfParity}%
                  </Typography>
                </Box>
                <Box sx={{ width: 2, height: 60, bgcolor: 'divider' }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                    platform average at parity
                  </Typography>
                  <Typography sx={{ fontSize: 36, fontWeight: 700, color: 'success.main' }}>
                    {revenueHooks.cvrAtParity}%
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 14, mb: 3 }}>
                Fixing parity could increase bookings by ~{revenueHooks.cvrImprovementPct}%
              </Typography>

              {/* Stats Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                {renderStatCard('Search Impressions', revenueHooks.totalImpressions, '(last 30 days)')}
                {renderStatCard(
                  'Shown Overpriced',
                  `${revenueHooks.outOfParityPct}%`,
                  `(${revenueHooks.impressionsOutOfParity} impressions)`,
                  SEVERITY_COLORS.severe,
                )}
                {renderStatCard('Current Orders', String(revenueHooks.actualOrders30d), '(last 30 days)')}
                {renderStatCard(
                  'Current Revenue',
                  fmtPriceRounded(Number(String(revenueHooks.actualRevenue30d).replace(/,/g, ''))),
                  '(last 30 days)',
                  undefined,
                  26,
                )}
              </Box>
            </>
          )}
        </Paper>

        <Box data-html2canvas-ignore>
          <Divider sx={{ my: 3 }} />
        </Box>

        {/* Section 2: Price Comparison Analysis */}
        <Paper elevation={0} sx={SECTION_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Price Comparison Analysis</Typography>
            <IconButton onClick={handleDownloadChart} title="Download chart as PNG">
              <DownloadIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Show/Hide Channels</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {TRACKED_OTAS.map((ota) => {
                const chipStyle = SUPPLIER_CHIP_COLORS[ota];
                if (!chipStyle) {
                  return null;
                }
                const active = channelVisibility[ota];
                return (
                  <Box
                    key={ota}
                    component="span"
                    onClick={() => toggleChannel(ota)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      fontSize: 13,
                      fontWeight: 600,
                      border: '2px solid',
                      cursor: 'pointer',
                      borderColor: chipStyle.border,
                      bgcolor: active ? chipStyle.bg : 'transparent',
                      color: chipStyle.color,
                      opacity: active ? 1 : 0.5,
                      userSelect: 'none',
                    }}
                  >
                    {active ? '✓ ' : ''}
                    {ota}
                  </Box>
                );
              })}
              <Box
                component="span"
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  fontSize: 13,
                  fontWeight: 600,
                  border: 2,
                  borderColor: 'grey.400',
                  bgcolor: 'grey.100',
                  color: 'text.primary',
                  opacity: 0.6,
                  cursor: 'default',
                  userSelect: 'none',
                }}
              >
                ✓ Luxury Escapes
              </Box>
              <Box
                component="span"
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  fontSize: 13,
                  fontWeight: 600,
                  border: 2,
                  borderColor: 'info.main',
                  bgcolor: 'info.light',
                  color: 'info.dark',
                  userSelect: 'none',
                }}
              >
                ✓ Best Competitor
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Box ref={chartSectionRef}>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={unifiedChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis domain={[a.yMin, a.yMax]} tickFormatter={fmtPriceRounded} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => (v != null ? fmtPrice(Number(v)) : '—')} />
                    {showLegend && <Legend />}
                    <Line
                      type="monotone"
                      dataKey={a.leColumn}
                      stroke={LE_CHART_COLOR}
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="Best Competitor"
                      stroke={BEST_COMPETITOR_CHART_COLOR}
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />
                    {TRACKED_OTAS.filter((ota) => channelVisibility[ota]).map((ota) => (
                      <Line
                        key={ota}
                        type="monotone"
                        dataKey={ota}
                        stroke={CHART_LINE_COLORS[ota]}
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              {totalDays > 1 && (
                <Box data-html2canvas-ignore>
                  <DateRangeSlider dates={a.dailyDetail.map((d) => d.date)} onRangeCommit={setDateRange} />
                </Box>
              )}
            </Box>
            <Box sx={{ width: 320, flexShrink: 0 }}>
              <RatePlanInfo
                ratePlans={ratePlans}
                isLoading={ratePlansLoading}
                error={ratePlansError}
                currency={a.currency}
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* PDF block 2: calendar -> end (page break before this block) */}
      <Box data-pdf-section data-pdf-page-break-before>
        <Divider sx={{ my: 3 }} />

        {/* Section 3: Competitive Calendar Matrix */}
        <Paper elevation={0} sx={SECTION_SX}>
          <CalendarRateView analysis={a} />
        </Paper>

        {a.dateRanges.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            {/* Section 4: Problem Check-in Date Ranges */}
            <Paper elevation={0} sx={SECTION_SX}>
              <Typography sx={{ fontSize: 24, fontWeight: 600, mb: 3 }}>Problem Check-in Date Ranges</Typography>
              <DataGrid
                rows={a.dateRanges.map((r, i) => ({ id: i, ...r }))}
                columns={dateRangeCols}
                autoHeight
                hideFooter={a.dateRanges.length <= 25}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                density="compact"
              />
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}
